import React, { useState } from 'react';
import { BygrModule, BygrProject, LicenseStatus, ProjectLicenseDto, SingleBygrModule } from 'types';
import { useAsyncState } from 'utils/hooks';
import { getProjectLicenseStatus, getProjectLicenses, setProjectLicenses } from 'services/licenses';
import {
    Button,
    IconToast,
    Input,
    LoadingIndicator,
    ValidationForm,
    arrayReplace,
    classnames,
} from '@ygtechnologies/bygr-frontend-component-library';
import { FormContainer } from 'components/Generic';
import { startCase } from 'lodash';
import theme from './ProjectLicenses.module.scss';

export interface ProjectLicensesProps {
    project: BygrProject;
}

const modules = [BygrModule.ChangeManagement, BygrModule.Aftermarket, BygrModule.QualityManagement].map(
    (x) => BygrModule[x] as SingleBygrModule
);

interface ProjectLicenseRowProps {
    license: ProjectLicenseDto;
    licenseStatus: LicenseStatus;
    onChange(newValue: ProjectLicenseDto): void;
}

const ProjectLicenseRow: React.FC<ProjectLicenseRowProps> = ({ license, licenseStatus, onChange }) => {
    const handleAllowedUnitsChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value === '' ? null : +e.target.value;
        onChange({ ...license, allowedUnits: value });
    };
    const handleNoteChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        onChange({ ...license, note: e.target.value });
    };

    return (
        <>
            <label>
                {startCase(license.module)}
                <div className="form-control-unstyled">
                    <span className={theme.actualUnits}>{licenseStatus.actualUnits} units &nbsp;/&nbsp;</span>
                    <Input
                        min={0}
                        value={license?.allowedUnits ?? ''}
                        type="number"
                        onChange={handleAllowedUnitsChange}
                        placeholder="Unlimited"
                        className="form-control"
                        style={{ width: '100px' }}
                    />
                    <span className={theme.actualUnits}>&nbsp;licenses</span>
                    <Input
                        placeholder="Note"
                        value={license?.note ?? ''}
                        className={classnames('form-control', theme.noteInput)}
                        type="text"
                        maxLength={2000}
                        onChange={handleNoteChange}
                    />
                </div>
            </label>
        </>
    );
};

export const ProjectLicensesUnits: React.FC<ProjectLicensesProps> = ({ project }) => {
    const [licenses, setLicenses] = useAsyncState(() => getProjectLicenses(project.id), [project.id], null);
    const [licensesStatus] = useAsyncState(() => getProjectLicenseStatus(project.id), [project.id], null);
    const [saving, setSaving] = useState(false);

    if (!licenses || !licensesStatus) {
        return <LoadingIndicator />;
    }

    const handleSave = async () => {
        setSaving(true);
        try {
            await setProjectLicenses(project.id, licenses);
            IconToast.success('Saved!');
        } finally {
            setSaving(false);
        }
    };

    const renderRow = (module: SingleBygrModule) => {
        const license = licenses.find((x) => x.module === module)!;
        const licenseStatus = licensesStatus.find((x) => x.module === module)!;

        return (
            <ProjectLicenseRow
                key={module}
                onChange={(newValue) => setLicenses(arrayReplace(licenses, license, newValue))}
                license={license}
                licenseStatus={licenseStatus}
            />
        );
    };

    return (
        <FormContainer maxSize="1200px">
            <ValidationForm className="form columns-1 labels-20" onSubmit={handleSave}>
                <ul>{modules.map((x) => renderRow(x))}</ul>

                <div className="buttons right">
                    <Button color="primary" type="submit" loading={saving}>
                        Save
                    </Button>
                </div>
            </ValidationForm>
        </FormContainer>
    );
};
